import dayjs from "dayjs";
import { Form, Formik } from "formik";

// import { DatePicker } from "antd";
// import dayjs from "dayjs";

import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import DatePicker, { DateObject } from "react-multi-date-picker";
import { useDispatch, useSelector } from "react-redux";

import ButtonLoader from "@/Components/ButtonLoader";
import FieldErrorMessage from "@/Components/common/FieldErrorMessage";
import { RequiredSymbol } from "@/Components/common/RequiredSymbol";
import {
  getDayWiseRates,
  updateActiveStep,
  updateJobData,
  updateRepeatObj,
  updateStepsValidated,
} from "@/reducers/clientcreatejob";
import { createJobValidations } from "@/utilities/CommonText";
import { showWarningToast } from "@/utilities/utils";

import ResetButton from "../buttons/ResetButton";
import { JobShiftLists } from "../JobShiftLists";
import { JobSteps } from "../JobSteps";
import DateRecurrence from "../selectDates/DateRecurrence";
import { Summary } from "../Summary";

/**
 * Component for selecting dates.
 *
 * @param {function} updateDays - Function to update days
 * @return {JSX.Element} The rendered JSX element
 */
export const SelectDates = ({ updateDays = "" }) => {
  const { jobData, getDayWiseApiLoading, isStartEndDateDisabled, jobShiftObj } =
    useSelector((state) => state.clientCreateJob);
  const [isDateChanged, setIsDateChanged] = useState(false);
  const dispatch = useDispatch();
  const [tempJobDates, setTempJobDates] = useState(jobData.selectDateJobDates);
  const [tempJobRanges, setTempJobRanges] = useState(jobData.selectDatesRanges);

  useEffect(() => {
    // updates the tempJobDates and tempJobRanges when the jobData.selectDateJobDates and jobData.selectDatesRanges changes
    // triggers when any date is removed due to no rates or BH
    if (jobData.selectDateJobDates !== tempJobDates) {
      setTempJobDates(jobData.selectDateJobDates);
    }
    if (jobData.selectDatesRanges !== tempJobRanges) {
      setTempJobRanges(jobData.selectDatesRanges);
    }
  }, [jobData.selectDateJobDates, jobData.selectDatesRanges]);

  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const initialUpdateJobData = useRef(true);
  const updateOnDateChange = (dates) => {
    const rateArray = Array.isArray(jobData?.rateCardList)
      ? jobData?.rateCardList
      : [jobData?.rateCardList];

    if (jobData.startTime && jobData.endTime && rateArray.length > 0) {
      let dayObj = {};
      dates.forEach((date) => {
        let day = dayjs(date, "DD-MM-YYYY").format("dddd");
        if (dayObj[day]) {
          dayObj[day].push(date);
        } else {
          dayObj[day] = [date];
        }
      });
      Object.keys(dayObj).forEach((day) => {
        dispatch(
          getDayWiseRates({
            day,
            selected_date: dayObj[day],
          }),
        );
      });
    }
  };
  useEffect(() => {
    if (initialUpdateJobData.current) {
      initialUpdateJobData.current = false;
      return;
    }
    if (jobData.startDate && jobData.endDate) {
      dispatch(
        updateJobData({
          totalDays: jobData.selectDateJobDates.length,
        }),
      );
      updateDays(updateOnDateChange, jobData.selectDateJobDates);
    }
  }, [jobData.startDate, jobData.endDate]);

  useEffect(() => {
    if (!jobData.startDate && !jobData.endDate) {
      setTempJobDates([]);
      setTempJobRanges([]);
    }
  }, []);
  const DatePickerRef = useRef(null);
  const getRangeDates = (startDate, endDate) => {
    startDate = moment(startDate, "DD-MM-YYYY");
    endDate = moment(endDate, "DD-MM-YYYY");
    let dates = [];
    while (startDate <= endDate) {
      dates.push(startDate.format("DD-MM-YYYY"));
      startDate = startDate.clone().add(1, "d");
    }
    return dates;
  };
  return (
    <div className="SelectDates_Wrapper">
      <div className="grid gap-4">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-[1fr,280px] xl:grid-cols-[1fr,330px]">
          <div className="grid h-fit gap-4">
            <JobSteps />
            <div className="rounded-lg bg-white p-6">
              <div className="mb-2 flex justify-between">
                <h4 className="text-[18px] font-medium leading-[normal] text-black">
                  Pick the dates for your locum position
                </h4>
                <div className="flex gap-4">
                  <ResetButton />
                </div>
              </div>
              <Formik
                initialValues={{
                  startDate: jobData.startDate || "",
                  endDate: jobData.endDate || "",
                }}
                validate={(values) => {
                  const errors = {};
                  if (!values.startDate) {
                    errors.startDate = createJobValidations.startDate;
                  }
                  if (!values.endDate) {
                    errors.endDate = createJobValidations.endDate;
                  }
                  return errors;
                }}
                enableReinitialize={true}
                onSubmit={(values, { setSubmitting }) => {
                  // if(tempJobDates.length !== jobData.selectDateJobDates.length) {
                  //   setSubmitting(false);
                  //   return
                  // }
                  initialUpdateJobData.current = true;
                  if (getDayWiseApiLoading) {
                    showWarningToast(createJobValidations.isFetchingRates, {
                      id: "isFetchingRates",
                    });
                    setSubmitting(false);
                    return;
                  }
                  dispatch(updateActiveStep(3));
                  dispatch(
                    updateStepsValidated({
                      timeSelection: true,
                    }),
                  );
                }}
              >
                {({
                  submitForm,
                  isSubmitting,
                  errors,
                  touched,
                  isValid,
                  values,
                  setFieldValue,
                }) => (
                  <Form>
                    <div className="grid grid-cols-1 gap-4">
                      <div className="relative flex flex-col">
                        <div className="relative z-0 grid grid-cols-2 gap-6 bg-white">
                          <div
                            className="flex flex-col gap-3"
                            ref={startDateRef}
                          >
                            <label className="label" htmlFor="startDate">
                              Start Date
                              <RequiredSymbol />
                            </label>
                            <div>
                              <input
                                className="ease h-[40px] w-full rounded-lg border border-lightGrey px-4 text-[14px] font-medium leading-[normal] text-black transition-all duration-200 placeholder:font-medium placeholder:text-txtColor focus:border-primary"
                                placeholder="DD-MM-YYYY"
                                // id="startDate"
                                value={values.startDate}
                                readOnly
                                onClick={() => {
                                  DatePickerRef.current.openCalendar();
                                }}
                              />
                              <FieldErrorMessage
                                name="startDate"
                                className={"text-red"}
                              />
                            </div>
                          </div>
                          <div className="flex flex-col gap-3" ref={endDateRef}>
                            <label className="label" htmlFor="startDate">
                              End Date
                              <RequiredSymbol />
                            </label>
                            <div>
                              <input
                                className="ease h-[40px] w-full rounded-lg border border-lightGrey px-4 text-[14px] font-medium leading-[normal] text-black transition-all duration-200 placeholder:font-medium placeholder:text-txtColor focus:border-primary"
                                value={values.endDate}
                                placeholder="DD-MM-YYYY"
                                // id="endeDate"
                                readOnly
                                onClick={() => {
                                  DatePickerRef.current.openCalendar();
                                }}
                              />
                              <FieldErrorMessage
                                name="endDate"
                                className={"text-red"}
                              />
                            </div>
                          </div>
                        </div>
                        {/* <div className="customRangePicker">
                            <RangePicker
                              format="DD-MM-YYYY"
                              placeholder={["DD-MM-YYYY", "DD-MM-YYYY"]}
                              popupClassName="createJobRangePicker"
                              disabledDate={(current) => {
                                return (
                                  current && current < moment().startOf("day")
                                );
                              }}
                              onChange={(value) => {
                                let startDate = value[0].format("DD-MM-YYYY");
                                let endDate = value[1].format("DD-MM-YYYY");
                                dispatch(updateJobData({ startDate, endDate }));
                              }}
                              value={[
                                values.startDate
                                  ? dayjs(values.startDate, "DD-MM-YYYY")
                                  : null,
                                values.endDate
                                  ? dayjs(values.endDate, "DD-MM-YYYY")
                                  : null,
                              ]}
                              suffixIcon={null}
                              prevIcon={
                                <span className="flex h-[24px] w-[24px] items-center justify-center rounded-lg !bg-[#EBEDF3] !text-black">
                                  <CalendarArrow />
                                </span>
                              }
                              superPrevIcon={null}
                              superNextIcon={null}
                              nextIcon={
                                <span className="flex h-[24px] w-[24px] -rotate-180 items-center justify-center rounded-lg !bg-[#EBEDF3] !text-black">
                                  <CalendarArrow />
                                </span>
                              }
                            />
                          </div> */}
                        <div className="createJobRangePickerContainer">
                          <DatePicker
                            ref={DatePickerRef}
                            multiple
                            range
                            // disabled={isStartEndDateDisabled}
                            weekDays={["S", "M", "T", "W", "T", "F", "S"]}
                            dateSeparator=" to "
                            multipleRangeSeparator="&"
                            numberOfMonths={2}
                            containerClassName="createJobRangePicker"
                            inputMode="input"
                            value={tempJobRanges.map((date) => {
                              let date1 = date[0].split("-");
                              let date2 =
                                date.length === 2 ? date[1].split("-") : [];
                              if (date2.length > 0) {
                                return [
                                  new DateObject({
                                    year: parseInt(date1[2]),
                                    month: parseInt(date1[1]),
                                    day: parseInt(date1[0]),
                                  }),
                                  new DateObject({
                                    year: parseInt(date2[2]),
                                    month: parseInt(date2[1]),
                                    day: parseInt(date2[0]),
                                  }),
                                ];
                              } else {
                                return [
                                  new DateObject({
                                    year: parseInt(date1[2]),
                                    month: parseInt(date1[1]),
                                    day: parseInt(date1[0]),
                                  }),
                                  new DateObject({
                                    year: parseInt(date1[2]),
                                    month: parseInt(date1[1]),
                                    day: parseInt(date1[0]),
                                  }),
                                ];
                              }
                            })}
                            minDate={new DateObject()}
                            onChange={(value) => {
                              let lastDate = value[value.length - 1];
                              let clickedDate = lastDate[0];
                              let clickedDateSTR = `${clickedDate.day.toString().padStart(2, "0")}-${clickedDate.month.number.toString().padStart(2, "0")}-${clickedDate.year}`;
                              let isAlreadySelected =
                                tempJobDates.includes(clickedDateSTR);
                              setIsDateChanged(true);
                              let datesArr = value.map((date) => {
                                let dates = [];
                                let sDate = date[0];
                                dates.push(
                                  `${sDate.day.toString().padStart(2, "0")}-${sDate.month.number.toString().padStart(2, "0")}-${sDate.year}`,
                                );
                                if (date.length === 1) {
                                  dates.push(
                                    `${sDate.day.toString().padStart(2, "0")}-${sDate.month.number.toString().padStart(2, "0")}-${sDate.year}`,
                                  );
                                } else if (date.length === 2) {
                                  let eDate = date[1];
                                  dates.push(
                                    `${eDate.day.toString().padStart(2, "0")}-${eDate.month.number.toString().padStart(2, "0")}-${eDate.year}`,
                                  );
                                }
                                return dates;
                              });
                              if (isAlreadySelected) {
                                datesArr = datesArr.filter((date) => {
                                  return date[0] !== clickedDateSTR;
                                });
                              }
                              setTempJobRanges(datesArr);
                              let dateRanges = datesArr.reduce((acc, date) => {
                                return acc.concat(
                                  getRangeDates(date[0], date[1]),
                                );
                              }, []);
                              const dateObjects = dateRanges.map(
                                (dateString) => {
                                  const [day, month, year] =
                                    dateString.split("-");
                                  return new Date(`${year}-${month}-${day}`);
                                },
                              );

                              // Sort the Date objects
                              dateObjects.sort((a, b) => a - b);

                              const sortedDates = dateObjects.map((date) => {
                                const day = date
                                  .getDate()
                                  .toString()
                                  .padStart(2, "0");
                                const month = (date.getMonth() + 1)
                                  .toString()
                                  .padStart(2, "0");
                                const year = date.getFullYear();
                                return `${day}-${month}-${year}`;
                              });
                              setTempJobDates(sortedDates);
                            }}
                            onClose={() => {
                              if (isDateChanged) {
                                dispatch(
                                  updateJobData({
                                    startDate: tempJobDates[0],
                                    endDate:
                                      tempJobDates[tempJobDates.length - 1],
                                    selectDatesRanges: tempJobRanges,
                                    selectDateJobDates: tempJobDates,
                                    totalDays: tempJobDates.length,
                                  }),
                                );
                                dispatch(
                                  updateRepeatObj({
                                    startsOn: tempJobDates[0],
                                    endsOn:
                                      tempJobDates[tempJobDates.length - 1],
                                  }),
                                );
                                updateDays(updateOnDateChange, tempJobDates);
                                setIsDateChanged(false);
                              }
                            }}
                            // plugins={[
                            //   <DatePanel
                            //     position={"right"}
                            //     sort="date"
                            //     eachDaysInRange={(date, i) => {
                            //       return (
                            //         <div
                            //           key={i}
                            //           className="text-xs text-black"
                            //         >
                            //           {date.format("DD")}
                            //         </div>
                            //       );
                            //     }}
                            //   />
                            // ]}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-6 bg-white">
                        <DateRecurrence />
                      </div>
                      <div className="flex justify-end gap-4">
                        <button
                          className="ease relative h-[40px] w-[120px] rounded-lg border border-primary bg-white text-[14px] font-medium leading-[38px] text-primary transition-all duration-200 hover:bg-none hover:bg-primary hover:text-white"
                          onClick={() => {
                            dispatch(updateActiveStep(1));
                          }}
                          type="button"
                        >
                          <span>Previous</span>
                        </button>
                        <button
                          className="ease relative h-[40px] w-[120px] rounded-lg border border-primary bg-primary text-[14px] font-medium leading-[38px] text-white transition-all duration-200 hover:bg-none hover:text-primary"
                          onClick={submitForm}
                          type="button"
                          disabled={isSubmitting || !isValid}
                        >
                          {isSubmitting ? <ButtonLoader /> : <span>Next</span>}
                        </button>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
          <Summary />
        </div>
        {jobData.totalDays > 0 && jobShiftObj.length ? <JobShiftLists /> : null}
      </div>
    </div>
  );
};
